import React, { useState, useRef, useEffect } from "react";
import { Share2, Mail, Copy, Check, MessageCircle, X } from "lucide-react";
import { trackEvent } from "@/lib/analytics";

/**
 * ShareButton — appears on retreat & training landing pages.
 *
 * Opens a small popover with three options:
 *   1. Copy link
 *   2. Open native share sheet (mobile / Web Share API)
 *   3. Pre-filled email (with subject + body)
 *   4. SMS (mobile-friendly, opens iMessage / default texts app)
 *
 * Fires `retreat_shared` analytics event with `{slug, title, share_method}`.
 */
export default function ShareButton({
  title,
  url,                  // full shareable URL (e.g. window.location.href)
  slug,                 // for analytics
  kind = "retreat",     // "retreat" | "training" — colors event taxonomy
  align = "right",      // popover alignment
  className = "",
  label = "Share with a sister",
}) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const ref = useRef(null);

  // Build pre-filled assets
  const fullUrl = url || (typeof window !== "undefined" ? window.location.href : "");
  const emailSubject = `Thought you'd love this — ${title}`;
  const emailBody = `I came across this and immediately thought of you.\n\n${title}\n${fullUrl}\n\nLet me know what you think — would be magical to go together. ✨`;
  const smsBody = `Thought of you — ${title}: ${fullUrl}`;

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, [open]);

  const track = (method) => {
    trackEvent("retreat_shared", {
      slug,
      title,
      kind,
      share_method: method,
      share_url: fullUrl,
    });
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      track("copy_link");
      setTimeout(() => setCopied(false), 1800);
    } catch (_e) {
      // fallback — selection-based copy
      const ta = document.createElement("textarea");
      ta.value = fullUrl;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); setCopied(true); track("copy_link"); setTimeout(() => setCopied(false), 1800); } catch {}
      document.body.removeChild(ta);
    }
  };

  const nativeShare = async () => {
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({ title, text: title, url: fullUrl });
        track("native_share");
      } catch (_e) { /* user cancelled — silent */ }
    } else {
      copyLink();
    }
  };

  const hasNativeShare = typeof navigator !== "undefined" && "share" in navigator;

  return (
    <div ref={ref} className={`relative inline-block ${className}`} data-testid="share-button-root">
      <button
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-aviana-gold/50 text-aviana-goldDeep hover:bg-aviana-gold/10 text-[12px] tracking-[0.22em] uppercase transition-all"
        data-testid="share-button-trigger"
        aria-expanded={open}
      >
        <Share2 size={14} />
        {label}
      </button>

      {open && (
        <div
          className={`absolute top-full mt-3 z-50 ${align === "right" ? "right-0" : "left-0"} w-[280px] bg-aviana-cream rounded-2xl border border-aviana-border shadow-[0_24px_60px_-20px_rgba(31,20,36,0.25)] p-2`}
          data-testid="share-button-popover"
        >
          <div className="px-3 pt-2 pb-3 flex items-center justify-between">
            <p className="overline-plum text-[10px]">Send to someone</p>
            <button onClick={() => setOpen(false)} className="text-aviana-muted hover:text-aviana-ink" aria-label="Close share menu">
              <X size={14} />
            </button>
          </div>

          {hasNativeShare && (
            <button
              onClick={() => { nativeShare(); setOpen(false); }}
              className="w-full text-left px-3 py-2.5 rounded-xl hover:bg-aviana-plum/5 flex items-center gap-3 transition-colors"
              data-testid="share-native"
            >
              <Share2 size={16} className="text-aviana-plum shrink-0" />
              <div>
                <p className="text-[13px] text-aviana-ink leading-tight">Share via…</p>
                <p className="text-[11px] text-aviana-muted">Phone share sheet</p>
              </div>
            </button>
          )}

          <a
            href={`mailto:?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`}
            onClick={() => { track("email"); setOpen(false); }}
            className="block px-3 py-2.5 rounded-xl hover:bg-aviana-plum/5 transition-colors"
            data-testid="share-email"
          >
            <div className="flex items-center gap-3">
              <Mail size={16} className="text-aviana-plum shrink-0" />
              <div>
                <p className="text-[13px] text-aviana-ink leading-tight">Email a friend</p>
                <p className="text-[11px] text-aviana-muted">Opens your mail app</p>
              </div>
            </div>
          </a>

          <a
            href={`sms:?&body=${encodeURIComponent(smsBody)}`}
            onClick={() => { track("sms"); setOpen(false); }}
            className="block px-3 py-2.5 rounded-xl hover:bg-aviana-plum/5 transition-colors"
            data-testid="share-sms"
          >
            <div className="flex items-center gap-3">
              <MessageCircle size={16} className="text-aviana-plum shrink-0" />
              <div>
                <p className="text-[13px] text-aviana-ink leading-tight">Text message</p>
                <p className="text-[11px] text-aviana-muted">Mobile-friendly</p>
              </div>
            </div>
          </a>

          <button
            onClick={copyLink}
            className="w-full text-left px-3 py-2.5 rounded-xl hover:bg-aviana-plum/5 flex items-center gap-3 transition-colors"
            data-testid="share-copy"
          >
            {copied ? <Check size={16} className="text-green-700 shrink-0" /> : <Copy size={16} className="text-aviana-plum shrink-0" />}
            <div>
              <p className="text-[13px] text-aviana-ink leading-tight">{copied ? "Link copied!" : "Copy link"}</p>
              <p className="text-[11px] text-aviana-muted truncate max-w-[210px]">{fullUrl.replace(/^https?:\/\//, "")}</p>
            </div>
          </button>
        </div>
      )}
    </div>
  );
}
